import { http } from '../http';
import { transformBlockListRow } from 'BlockList/utils/transformBlockListRow';
import type {
  AttributeTypeKey,
  BlockedAttribute,
  BlockListRow,
  ReasonKey,
} from 'BlockList/types';

// ---------------------------------------------------------------------------
// Request shapes (snake_case wire format)
// ---------------------------------------------------------------------------

type AttributeValuesEmail = { email: string };
type AttributeValuesEmailDomain = { email_domain: string };
type AttributeValuesPhoneNumber = { phone_number: string };
type AttributeValuesVin = { vin: string };
type AttributeValuesDriversLicense = { state: string; number: string };
type AttributeValuesNameDob = {
  first_name: string;
  last_name: string;
  dob: string; // YYYY-MM-DD
};

export type AttributeValues =
  | AttributeValuesEmail
  | AttributeValuesEmailDomain
  | AttributeValuesPhoneNumber
  | AttributeValuesVin
  | AttributeValuesDriversLicense
  | AttributeValuesNameDob;

export type CreateBlockedAttributePayload = {
  attribute_type: AttributeTypeKey;
  attribute_values: AttributeValues;
  reason: ReasonKey;
  notes: string;
  policy_number?: string;
};

// ---------------------------------------------------------------------------
// Response shape (snake_case wire) — wrapped in { data: BlockedAttribute }
// ---------------------------------------------------------------------------

type ServerResponse = {
  data: BlockedAttribute;
};

// ---------------------------------------------------------------------------
// Service function — transforms wire → domain (BlockListRow) so consumers
// receive the same camelCase shape as Get/Deactivate.
// ---------------------------------------------------------------------------

export const postBlockListAttribute = (
  token: string,
  payload: CreateBlockedAttributePayload
) => {
  // Build request body — omit policy_number when blank
  const body: Record<string, unknown> = {
    attribute_type: payload.attribute_type,
    attribute_values: payload.attribute_values,
    reason: payload.reason,
    notes: payload.notes,
  };
  if (payload.policy_number && payload.policy_number.trim() !== '') {
    body.policy_number = payload.policy_number.trim();
  }

  return http.post<BlockListRow>(
    {
      url: '/clearcover_admin/blocklist/attributes',
      token,
      transformResponse: (jsonData: unknown): unknown => {
        const raw =
          typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
        // axios runs transformResponse on every status code; error payloads
        // (e.g. 422 SimpleErrorResponse) pass through untouched so the catch
        // path can read the server message via extractReznorError.
        if (
          !raw ||
          typeof raw !== 'object' ||
          !(raw as { data?: unknown }).data
        ) {
          return raw;
        }
        return transformBlockListRow((raw as ServerResponse).data);
      },
    },
    body
  );
};
